// 配布版の最後 (「つづく」2枚目) に出す、感想を一言書ける欄。
//
// なぜ canvas でなく DOM か: 日本語を打つには IME が要る。 canvas に自前の入力欄を
//   作ると変換候補が出せず、実質かな入力ができない。 #stage は position:relative なので
//   その中に % で置けば、 ウィンドウを伸縮しても canvas と一緒に拡縮する。
//
// なぜ「送信」でなく「コピー」か: サーバーを持たないため。 クリップボードへ入れて
//   LINE や Discord へそのまま貼ってもらうのが、 配布版で唯一確実に届く経路。
//   ⚠️ 勝手にどこかへ送らない = 押すまで何も外へ出ない。
//
// 下書きは localStorage に残す (書いている途中で閉じても消えないように)。
//
// なぜ末尾に刻印を付けるか (2026-07-26): 本番は Cloudflare Pages が main から自動デプロイする
//   ため、 main へ入れた瞬間に友達の遊ぶ版が変わる。 刻印が無いと、 届いた感想が
//   **どの版・どの難易度のものか永久に分からない**。 版と幕と「倒れた回数」だけを添える。
//   ⚠️ 勝手に送らない方針は変えない = 押した時にクリップボードへ入るだけ。
//   ⚠️ 何が一緒にコピーされるかは画面に出す (黙って足さない)。

import type { CampaignState, Difficulty } from './types'
import { BOSS_DEFINITIONS } from './bosses'
import { getDemoFinalBossId } from './campaign'
import { currentRecordText } from './playtest_record'
import { retryLabels } from './adv_fix2'

const DRAFT_KEY = 'shinige.feedback.draft'
const PANEL_ID = 'feedback-panel'

const DIFFICULTY_LABEL: Record<Difficulty, string> = {
  senshuraku: '千秋楽',
  hatsuen: '初演',
}

// テスト環境 (vitest) は vite.config.ts の define を通らないので未定義になり得る。
function buildId(): string {
  return typeof __BUILD_ID__ === 'string' ? __BUILD_ID__ : 'unknown'
}
function buildDate(): string {
  return typeof __BUILD_DATE__ === 'string' ? __BUILD_DATE__ : 'unknown'
}

export type FeedbackContext = {
  difficulty: Difficulty
  deaths: { label: string; count: number }[]
  // 書き終えた後にどこへ出るか、のボタン文言。 既定 (配布版の最後) は「タイトルへ」。
  //   一戦だけモード (?fight=) は行き先が「同じボスをもう一度」なので差し替える (2026-07-28)。
  closeLabel?: string
  // パネルの縦位置 (#stage 内の %)。 既定 58% = 「つづく」カードの本文の下。
  //   一戦だけモードは撃破画面に出す = 画面下にボスHPバー等のHUDが残っているので、
  //   重ならない高さまで上げる (2026-07-28 実表示で衝突を確認して調整)。
  top?: string
}

// 配布範囲 (DEMO_FINAL_BOSS_ID まで) のボスだけを、進行順で並べる。
//   範囲外のボスを載せると、遊べないボスの名前が感想に混ざってネタバレになる。
export function buildFeedbackContext(campaign: CampaignState, difficulty: Difficulty): FeedbackContext {
  const finalId = getDemoFinalBossId()
  const endIndex = finalId ? BOSS_DEFINITIONS.findIndex((b) => b.id === finalId) : -1
  const inRange = endIndex >= 0 ? BOSS_DEFINITIONS.slice(0, endIndex + 1) : BOSS_DEFINITIONS
  return {
    difficulty,
    deaths: inRange.map((b) => ({ label: b.name, count: campaign.deathsByBossId[b.id] ?? 0 })),
  }
}

// 一戦だけモード (?fight=・2026-07-28) の感想欄。 載せるのは渡したボス1体だけ。
//   配布版と同じ規律 = 遊べないボスの名前を感想へ混ぜない (ネタバレを作らない)。
export function buildSoloFeedbackContext(
  campaign: CampaignState, difficulty: Difficulty, bossId: string,
): FeedbackContext {
  const def = BOSS_DEFINITIONS.find((b) => b.id === bossId)
  return {
    difficulty,
    deaths: def ? [{ label: def.name, count: campaign.deathsByBossId[bossId] ?? 0 }] : [],
    closeLabel: retryLabels().close,
    top: '40%',
  }
}

// 感想本文の末尾に足す刻印。 本文が空の時は呼ばない (コピー自体をしない)。
export function buildFeedbackFooter(ctx: FeedbackContext): string {
  const deaths = ctx.deaths.map((d) => `${d.label} ${d.count}`).join(' ／ ')
  return [
    '――――――',
    `版: ${buildId()} / ${buildDate()}`,
    `幕: ${DIFFICULTY_LABEL[ctx.difficulty]}`,
    `倒れた回数: ${deaths}`,
  ].join('\n')
}

let panel: HTMLDivElement | null = null

function loadDraft(): string {
  try { return localStorage.getItem(DRAFT_KEY) ?? '' } catch { return '' }
}
function saveDraft(v: string): void {
  try { localStorage.setItem(DRAFT_KEY, v) } catch { /* 保存できなくても入力は続けられる */ }
}

function build(ctx: FeedbackContext): HTMLDivElement {
  const root = document.createElement('div')
  root.id = PANEL_ID
  root.style.cssText = [
    'position:absolute', 'left:50%', `top:${ctx.top ?? '58%'}`, 'transform:translateX(-50%)',
    'width:46%', 'display:flex', 'flex-direction:column', 'gap:0.6em',
    'font-family:"Yu Gothic UI","Yu Gothic","Hiragino Kaku Gothic ProN","Noto Sans JP",sans-serif',
    'font-size:clamp(11px,1.35vw,17px)', 'color:#c6c4ce', 'z-index:10',
  ].join(';')

  // パネルの中の操作はゲームへ渡さない。 ボタンのクリックは window の mousedown を
  // 経由して「攻撃」になり、 そのままタイトルへ飛んでしまう (実際に踏んだ)。
  // isTypingTarget (input.ts) はタグで見るのでボタンを守れない = ここで止めるのが確実。
  for (const type of ['mousedown', 'mouseup', 'keydown', 'keyup', 'contextmenu']) {
    root.addEventListener(type, (e) => e.stopPropagation())
  }

  const label = document.createElement('div')
  label.textContent = 'よかったら、一言だけ感想を。（書かなくても大丈夫です）'
  label.style.cssText = 'opacity:0.72;letter-spacing:0.04em'

  const area = document.createElement('textarea')
  area.rows = 3
  area.value = loadDraft()
  area.placeholder = '難しかった / ここが気持ちよかった / ここで迷った …'
  area.style.cssText = [
    'width:100%', 'box-sizing:border-box', 'resize:none',
    'background:rgba(255,255,255,0.05)', 'border:1px solid rgba(255,255,255,0.18)',
    'border-radius:3px', 'padding:0.6em 0.7em', 'color:#e4e2ea',
    'font:inherit', 'line-height:1.7', 'outline:none',
  ].join(';')
  area.addEventListener('input', () => saveDraft(area.value))
  area.addEventListener('focus', () => { area.style.borderColor = 'rgba(255,255,255,0.4)' })
  area.addEventListener('blur', () => { area.style.borderColor = 'rgba(255,255,255,0.18)' })

  const row = document.createElement('div')
  row.style.cssText = 'display:flex;align-items:center;gap:1em'

  const copy = document.createElement('button')
  copy.type = 'button'
  copy.textContent = 'コピーする'
  copy.style.cssText = [
    'background:rgba(255,255,255,0.07)', 'border:1px solid rgba(255,255,255,0.22)',
    'border-radius:3px', 'padding:0.45em 1.3em', 'color:#e4e2ea',
    'font:inherit', 'cursor:pointer',
  ].join(';')

  const note = document.createElement('span')
  note.style.cssText = 'opacity:0.6;font-size:0.9em'

  copy.addEventListener('click', async () => {
    // 感想が空でも押せる = 遊んだ記録だけを写す (作者 10-30「プレイヤーに書かせるのは大変」)。
    const text = area.value.trim()
    const payload = `${text ? text + '\n\n' : ''}${buildFeedbackFooter(ctx)}\n${currentRecordText(buildId())}`
    try {
      await navigator.clipboard.writeText(payload)
    } catch {
      // クリップボードが使えない環境 (権限・古いブラウザ) では選択状態にして手動コピーへ促す。
      // ⚠️ この経路では刻印が付かない (textarea の中身しか選べない)。 届いた感想に版が
      //    無ければ「手動コピーだった」と読める = 黙って別物になるより良い。
      area.select()
      note.textContent = 'コピーできませんでした。選択したので Ctrl+C を押してください。'
      return
    }
    note.textContent = text ? 'コピーしました。そのまま送ってもらえると嬉しいです。' : '遊んだ記録をコピーしました。そのまま送ってもらえると嬉しいです。'
  })

  // 入力欄にカーソルがある間はパネルが打鍵を止めるので、 画面下の「タイトルへ」の
  // キー操作が効かない = 出口が無いように見える。 明示のボタンで必ず出られるようにする。
  const close = document.createElement('button')
  close.type = 'button'
  close.textContent = ctx.closeLabel ?? 'タイトルへ'
  close.style.cssText = [
    'background:none', 'border:none', 'padding:0.45em 0.2em',
    'color:#8f8a9c', 'font:inherit', 'cursor:pointer', 'margin-left:auto',
  ].join(';')
  close.addEventListener('click', () => { closeRequested = true })

  row.append(copy, note, close)

  // 何が一緒にコピーされるかを、押す前に見せる (黙って足さない)。
  const stamp = document.createElement('div')
  stamp.textContent = `一緒にコピーされます — 版 ${buildId()} ／ ${DIFFICULTY_LABEL[ctx.difficulty]} ／ 倒れた回数 ／ ボスごとの戦績（挑んだ数・死因・時間）`
  stamp.style.cssText = 'opacity:0.45;font-size:0.82em;letter-spacing:0.03em'

  root.append(label, area, row, stamp)
  return root
}

// 出口ボタンが押された時に、 main.ts が1tickだけ流し込む「決定」キー。
//   ⚠️ 両方が要る。 game.ts の confirmPressed は パッド='retry' / kbm='attack' と device で
//      見るキーが違うため、 片方だけだともう片方の機器で**ボタンが無反応になる** (2026-07-28 実測)。
//   回帰ガード = tests/feedback_close_button.test.ts
export const FEEDBACK_CLOSE_KEYS = ['retry', 'attack'] as const

// 「タイトルへ」が押されたか。 main.ts が1tickだけ進行入力として流し込む
// (ここで phase を直接書き換えると、 幕の遷移規則が game.ts と二重管理になる)。
let closeRequested = false
// テスト用: ボタンを押した状態を作る (DOM を起こさずに約束だけ確かめる)。
export function requestFeedbackCloseForTest(): void { closeRequested = true }
export function consumeFeedbackClose(): boolean {
  if (!closeRequested) return false
  closeRequested = false
  return true
}

// phase に合わせて出し入れする。 main.ts の毎フレームから呼ばれるので、
// 状態が変わっていない時は何もしない (DOM を毎フレーム触らない)。
export function syncFeedbackPanel(visible: boolean, ctx: FeedbackContext): void {
  const stage = document.getElementById('stage')
  if (!stage) return
  if (visible && !panel) {
    // ctx はパネルを建てる瞬間の値で固定する。 感想欄は配布版の最後 (tsuzukuNote) にしか
    //   出ず、そこから戦績は動かないため、毎フレーム作り直す必要がない。
    panel = build(ctx)
    stage.appendChild(panel)
  } else if (!visible && panel) {
    panel.remove()
    panel = null
  }
}

// dev / 検収用: 書かれた下書きを読む。
export function readFeedbackDraft(): string { return loadDraft() }
